import React, { FC } from 'react'
import { GetServerSideProps } from 'next'
import useSWR from 'swr'

import { Layout } from 'components/layout'
import { ArticlesBlock } from '@components/articles-block'
import { HeadMetadata } from 'components/head-metadata'
import { SponsorBanner } from 'components/sponsor-banner'
import { HeaderSection } from '@components/header-section'
import { Divider } from '@components/divider'
import { Pagination } from '@components/shared/pagination'
import { TagsCloud } from '@components/tags-cloud'
import { SimulationBanner as SimulationBannerV2 } from '@components/v2/SimulationBanner'
import { CalculatorSolarProvider } from 'context/CalculatorSolarContext'
import { useWindowDimensions } from '@hooks/use-window-dimensions'

import { MenusApi, Menu } from '@services/api/menus'
import { ArticlesApi } from '@services/api/articles'
import restFetch from '@services/rest-fetch'
import { api } from '@services/api'

import { BlockCall } from 'types/block-call'
import { ArticlesIndexData } from 'types/articleIndexData'
import { Tag } from 'types/article'

import { convertObjectQueryParams } from '@utils/convert-object-query-params'
import { fetcherDataApi } from '@utils/fetcher-data-api'
import { AdsApi } from '@services/api/ads'
import { AdsIndexDto } from '@services/api/ads/types'

interface Props {
  data: ArticlesIndexData
  tags: Tag[]
  url: string
  blockCalls: BlockCall
  main: Menu[]
  footer1: Menu[]
  footer2: Menu[]
  ads: AdsIndexDto[]
  simulatorDesktopImages: BlockCall[]
  simulatorMobileImages: BlockCall[]
}

const LastNewsPage: FC<Props> = ({
  data: initialData,
  tags,
  url,
  blockCalls,
  main,
  footer1,
  footer2,
  ads,
  simulatorDesktopImages,
  simulatorMobileImages
}) => {
  const { width } = useWindowDimensions()
  const { data } = useSWR(url, fetcherDataApi, {
    initialData
  })
  const { articles, total, page, pageSize } = data

  return (
    <Layout
      sponsorKeyword="tag"
      mainMenus={main}
      footerMenus1={footer1}
      footerMenus2={footer2}
      title="Últimas Notícias de Energia Solar"
      logoHeading={true}
      logoUrl="/noticias"
      ad={ads.find(ad => ad.code === 'ad_sponsor_top')}
    >
      <HeadMetadata
        title="Últimas notícias"
        metaTitle="Últimas notícias"
        metaDescription="Últimas notícias - Portal Solar"
      />
      <HeaderSection className="py-4">
        <h2>Últimas Notícias</h2>
      </HeaderSection>
      <ArticlesBlock articles={articles.slice(0, 3)} className="grid-cols-3" />
      <SponsorBanner
        config={ads.find(ad => ad.code === 'ad_sponsor_middle')}
        className="mt-10"
      />

      <div className="mt-10">
        <CalculatorSolarProvider>
          <SimulationBannerV2
            windowWidth={width}
            disableSnap={true}
            desktopImages={simulatorDesktopImages}
            mobileImages={simulatorMobileImages}
            title={<p></p>}
            headingLevel="p"
            logo={null}
          />
        </CalculatorSolarProvider>
      </div>

      <div className="my-10">
        <Divider type="primary" orientation="horizontal" />
      </div>

      <ArticlesBlock articles={articles.slice(3)} className="grid-cols-4" />

      <Pagination currentPage={page} pageSize={pageSize} total={total} />

      <SponsorBanner
        config={ads.find(ad => ad.code === 'ad_sponsor_bottom')}
        className="my-10"
      />

      <TagsCloud tags={tags} className="my-10" />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const page = Number(query.page) || 1
  const paramsPagination = {
    page,
    per: 19
  }
  const amount = Number(query.amount) || 16

  const [
    { data },
    { data: tags },
    [blockCalls = null],
    menus,
    {
      data: { ads }
    }
  ] = await Promise.all([
    ArticlesApi.index(paramsPagination),
    api.get(`/tags?amount=${amount}`),
    restFetch<[{ imageUrl: string; imageAlt: string }]>(
      '/block_calls/random?amount=1&block_type=simulator'
    ),
    MenusApi.all(),
    AdsApi.index({
      codes: ['ad_sponsor_top', 'ad_sponsor_middle', 'ad_sponsor_bottom']
    })
  ])

  const url = convertObjectQueryParams({
    path: '/articles',
    object: paramsPagination
  })

  // Buscar imagens do simulador
  const simulatorDesktopResponse = await restFetch<{
    blockCalls: BlockCall[]
  }>('/block_calls?block_type=simulator')
  const simulatorDesktopImages = simulatorDesktopResponse?.blockCalls || []

  const simulatorMobileResponse = await restFetch<{
    blockCalls: BlockCall[]
  }>('/block_calls?block_type=simulator_image_mobile')
  const simulatorMobileImages = simulatorMobileResponse?.blockCalls || []

  return {
    props: {
      data,
      ...tags,
      url,
      blockCalls,
      ...menus,
      ads,
      simulatorDesktopImages,
      simulatorMobileImages,
      key: Number(new Date())
    }
  }
}

export default LastNewsPage
